import { getUserMetadataBySubId, getUserInfoBySubId } from './api/tenantApi';

// Simple cache for user metadata to avoid multiple API calls
let userMetadataCache = {};
let userInfoCache = {};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Retry a fetch a few times before giving up, so a transient failure (e.g. a
// backend pod restarting during a deploy) doesn't permanently mis-load the
// user's metadata — which would silently demote an admin to a tenant view.
const withRetry = async (fn, { attempts = 3, baseDelay = 500 } = {}) => {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) await sleep(baseDelay * (i + 1));
    }
  }
  throw lastErr;
};

const getUserMetadataWithCache = async (subId) => {
  if (!subId) return null;

  // Return cached data if available and not expired (5 minutes)
  if (userMetadataCache[subId] && Date.now() - userMetadataCache[subId].timestamp < 5 * 60 * 1000) {
    console.log('🚀 Using cached user metadata for subId:', subId);
    return userMetadataCache[subId].data;
  }

  try {
    console.log('📡 Fetching fresh user metadata for subId:', subId);
    const data = await withRetry(() => getUserMetadataBySubId(subId));

    // Cache the result (only successful fetches are cached)
    userMetadataCache[subId] = {
      data,
      timestamp: Date.now()
    };
    // Persist last-known-good so a transient failure can't demote the user.
    try { localStorage.setItem(`crediarc_user_meta_${subId}`, JSON.stringify(data)); } catch (_) {}

    return data;
  } catch (error) {
    console.error("Failed to fetch user metadata after retries:", error);
    // Fall back to the last successful fetch (e.g. backend restarting mid-deploy)
    // instead of returning null — which would wrongly drop an admin to a tenant view.
    try {
      const persisted = JSON.parse(localStorage.getItem(`crediarc_user_meta_${subId}`) || 'null');
      if (persisted) {
        console.warn('Using last-known-good user metadata from localStorage');
        return persisted;
      }
    } catch (_) {}
    return null;
  }
};

const getUserInfoWithCache = async (subId) => {
  if (!subId) return null;
  
  // Return cached data if available and not expired (5 minutes)
  if (userInfoCache[subId] && Date.now() - userInfoCache[subId].timestamp < 5 * 60 * 1000) {
    console.log('🚀 Using cached user info for subId:', subId);
    return userInfoCache[subId].data;
  }
  
  try {
    console.log('📡 Fetching fresh user info for subId:', subId);
    const data = await withRetry(() => getUserInfoBySubId(subId));

    // Cache the result
    userInfoCache[subId] = {
      data,
      timestamp: Date.now()
    };

    return data;
  } catch (error) {
    console.error("Failed to fetch user info after retries:", error);
    return null;
  }
};

export const getTenantIdAuth0 = async () => {
  const subId = localStorage.getItem("subId");
  if (!subId) return null;
  try {
    const meta = await getUserMetadataWithCache(subId);
    return meta?.tenantId ?? null;
  } catch (error) {
    console.error("Failed to fetch tenantId from backend:", error);
    return null;
  }
};

export const getEmailAuth0 = async () => {
  const subId = localStorage.getItem("subId");
  if (!subId) return null;
  try {
    const user = await getUserInfoWithCache(subId);
    return user?.email ?? null;
  } catch (error) {
    console.error("Failed to fetch email from backend:", error);
    return null;
  }
};

export const isAdminAuth0 = async () => {
  const subId = localStorage.getItem("subId");
  if (!subId) return false;
  try {
    const meta = await getUserMetadataWithCache(subId);
    return meta?.isAdmin ?? false;
  } catch (error) {
    console.error("Failed to fetch isAdmin from backend:", error);
    return false;
  }
};

// Distribution-partner identity — { isDistributor, distributorId } from the user's metadata.
export const getDistributorAuth0 = async () => {
  const subId = localStorage.getItem("subId");
  if (!subId) return { isDistributor: false, distributorId: null };
  try {
    const meta = await getUserMetadataWithCache(subId);
    return { isDistributor: !!meta?.isDistributor, distributorId: meta?.distributorId ?? null };
  } catch (error) {
    console.error("Failed to fetch distributor identity from backend:", error);
    return { isDistributor: false, distributorId: null };
  }
};

export const getTenantRole = async () => {
  // Implement if you have a backend endpoint for roles
  return null;
};

// Function to get current user ID (subId)
export const getCurrentUserId = () => {
  return localStorage.getItem("subId");
};

// Function to clear cache (useful for logout or user change)
export const clearUserCache = () => {
  userMetadataCache = {};
  userInfoCache = {};
  console.log('🧹 User metadata cache cleared');
};

// Hook to use these functions in React components
export const useAuthUtils = () => {
  // No need for getAccessTokenSilently here anymore
  return {
    getTenantId: getTenantIdAuth0,
    getEmail: getEmailAuth0,
    isAdmin: isAdminAuth0,
    getTenantRole: getTenantRole,
    clearCache: clearUserCache,
  };
};

